import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'

import { isPwaEnabled } from '@/api/config'

import {
  clearInstallPrompt,
  getInstallPrompt,
  subscribeInstallPrompt
} from './install-prompt-store'
import {
  dismissInstall,
  isInstallDismissed,
  isIosBrowser,
  isStandaloneDisplay
} from './utils'

/* prompt: chromium handed us its install event. ios: no api, the user has to
 * use share > add to home screen. none: already installed, or unsupported. */
export type InstallPlatform = 'prompt' | 'ios' | 'none'

export function useInstallPrompt() {
  const deferred = useSyncExternalStore(
    subscribeInstallPrompt,
    getInstallPrompt,
    () => null
  )
  const [standalone, setStandalone] = useState(isStandaloneDisplay)
  const [dismissed, setDismissed] = useState(isInstallDismissed)

  useEffect(() => {
    const media = window.matchMedia('(display-mode: standalone)')
    const update = () => setStandalone(isStandaloneDisplay())
    media.addEventListener('change', update)
    window.addEventListener('appinstalled', update)
    return () => {
      media.removeEventListener('change', update)
      window.removeEventListener('appinstalled', update)
    }
  }, [])

  /* PWA_ENABLED off reads as 'none', so the sidebar row, the settings card and
   * the ios sheet they own all disappear together. */
  const platform: InstallPlatform =
    !isPwaEnabled() || standalone
      ? 'none'
      : deferred
        ? 'prompt'
        : isIosBrowser()
          ? 'ios'
          : 'none'

  const install = useCallback(async () => {
    const event = getInstallPrompt()
    if (!event) return 'dismissed' as const
    await event.prompt()
    const { outcome } = await event.userChoice
    /* the event is single-use either way */
    clearInstallPrompt()
    return outcome
  }, [])

  const dismiss = useCallback(() => {
    dismissInstall()
    setDismissed(true)
  }, [])

  return { platform, dismissed, install, dismiss }
}
